import {useEffect,useState,useCallback,useMemo,useRef} from "react";
import {Link} from "react-router-dom";
import {ArrowRight,Calendar as CalIcon,ChevronLeft,ChevronRight,Download,LayoutGrid,LogOut,MessageCircle,Percent,Phone,Plus,Settings as Cog,Trash2,Upload,User,Users,BarChart3,Image as ImageIcon,Inbox,Star,X,Menu} from "lucide-react";
import {API} from "./App";

const TOKEN_KEY="smit_admin_token";
const useToken=()=>{
  const [token,setToken]=useState(sessionStorage.getItem(TOKEN_KEY)||"");
  const set=t=>{sessionStorage.setItem(TOKEN_KEY,t||"");setToken(t||"")};
  return [token,set];
};

const authFetch=async(token,path,opts={})=>{
  const r=await fetch(`${API}${path}`,{...opts,headers:{"Content-Type":"application/json",...(opts.headers||{}),Authorization:`Bearer ${token}`}});
  if(r.status===401){sessionStorage.removeItem(TOKEN_KEY);window.location.reload()}
  return r;
};

const TABS=[
  {id:"bookings",label:"Bookings",icon:CalIcon},
  {id:"menu",label:"Menu",icon:LayoutGrid},
  {id:"availability",label:"Availability",icon:CalIcon},
  {id:"offers",label:"Offers",icon:Percent},
  {id:"settings",label:"Settings",icon:Cog},
  {id:"enquiries",label:"Enquiries",icon:Inbox},
  {id:"gallery",label:"Gallery",icon:ImageIcon},
  {id:"clients",label:"Clients",icon:Users},
  {id:"analytics",label:"Analytics",icon:BarChart3},
  {id:"testimonials",label:"Testimonials",icon:Star},
];

export default function Admin(){
  const [token,setToken]=useToken();
  const [tab,setTab]=useState(sessionStorage.getItem("smit_admin_tab")||"bookings");
  const [navOpen,setNavOpen]=useState(false);
  useEffect(()=>{sessionStorage.setItem("smit_admin_tab",tab);setNavOpen(false)},[tab]);
  if(!token) return <LoginScreen onLogin={setToken}/>;
  return <div className="admin-shell">
    <aside className={`admin-sidebar${navOpen?" open":""}`}>
      <Link className="admin-brand" to="/" data-testid="admin-brand-link">
        <span>SMIT STUDIO</span><small>OWNER DASHBOARD</small>
      </Link>
      <nav>
        {TABS.map(t=><button key={t.id} className={tab===t.id?"active":""} onClick={()=>setTab(t.id)} data-testid={`admin-tab-${t.id}`}>
          <t.icon size={16}/><span>{t.label}</span>
        </button>)}
      </nav>
      <button className="admin-logout" onClick={()=>{setToken("")}} data-testid="admin-logout"><LogOut size={15}/> Sign out</button>
    </aside>
    <main className="admin-main">
      <header className="admin-topbar">
        <button className="admin-navtoggle" onClick={()=>setNavOpen(!navOpen)} data-testid="admin-mobile-menu"><Menu size={18}/></button>
        <h1>{TABS.find(t=>t.id===tab)?.label}</h1>
        <Link to="/" className="admin-viewsite" data-testid="admin-viewsite-link">View site <ArrowRight size={14}/></Link>
      </header>
      {tab==="bookings"&&<Bookings token={token}/>}
      {tab==="menu"&&<MenuManager token={token}/>}
      {tab==="availability"&&<AvailabilityPanel token={token}/>}
      {tab==="offers"&&<OffersManager token={token}/>}
      {tab==="settings"&&<SettingsPanel token={token}/>}
      {tab==="enquiries"&&<EnquiriesInbox token={token}/>}
      {tab==="gallery"&&<GalleryManager token={token}/>}
      {tab==="clients"&&<ClientsCRM token={token}/>}
      {tab==="analytics"&&<Analytics token={token}/>}
      {tab==="testimonials"&&<Testimonials token={token}/>}
    </main>
  </div>;
}

function LoginScreen({onLogin}){
  const [f,setF]=useState({username:"",password:""}),[err,setErr]=useState(""),[busy,setBusy]=useState(false);
  const go=async e=>{
    e.preventDefault();setErr("");setBusy(true);
    try{
      const r=await fetch(`${API}/auth/login`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(f)});
      const d=await r.json();
      if(r.ok) onLogin(d.token); else setErr(d.detail||"Unable to sign in right now.");
    }catch{setErr("Network issue. Please try again.")}
    setBusy(false);
  };
  return <div className="admin-login">
    <div className="admin-login-inner">
      <Link className="admin-brand" to="/"><span>SMIT STUDIO</span><small>OWNER DASHBOARD</small></Link>
      <p className="eyebrow">Welcome back</p>
      <h1>Sign in to run<br/><em>the studio.</em></h1>
      <form onSubmit={go}>
        <label>Username<input value={f.username} onChange={e=>setF({...f,username:e.target.value})} data-testid="admin-username-input" autoFocus/></label>
        <label>Password<input type="password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} data-testid="admin-password-input"/></label>
        {err&&<p className="admin-error" role="alert" data-testid="admin-login-error">{err}</p>}
        <button className="button gold" disabled={busy} data-testid="admin-login-button">{busy?"Signing in...":"Enter dashboard"} <ArrowRight size={16}/></button>
      </form>
      <p className="admin-note">Admin credentials are set in your backend env. Rotate the password by updating <code>ADMIN_PASSWORD_HASH</code>.</p>
    </div>
  </div>;
}

// ---------- Bookings ----------
function Bookings({token}){
  const [items,setItems]=useState([]);
  const [filter,setFilter]=useState({status:"",from:"",to:""});
  const [showAdd,setShowAdd]=useState(false);
  const [showEdit,setShowEdit]=useState(null);
  const load=useCallback(async()=>{
    const qs=new URLSearchParams();
    if(filter.status) qs.set("status",filter.status);
    if(filter.from) qs.set("date_from",filter.from);
    if(filter.to) qs.set("date_to",filter.to);
    const r=await authFetch(token,`/admin/bookings?${qs}`);
    setItems(await r.json());
  },[token,filter]);
  useEffect(()=>{load()},[load]);
  const today=new Date().toISOString().slice(0,10);
  const todayCount=items.filter(x=>x.date===today).length;
  const upcoming=items.filter(x=>x.date>=today&&!["cancelled","no_show"].includes(x.status)).length;
  const pending=items.filter(x=>x.status==="pending").length;
  const setStatus=async(id,status)=>{await authFetch(token,`/admin/bookings/${id}`,{method:"PATCH",body:JSON.stringify({status})});load()};
  const remove=async id=>{if(!confirm("Delete this booking?"))return;await authFetch(token,`/admin/bookings/${id}`,{method:"DELETE"});load()};
  const exportCsv=()=>{fetch(`${API}/admin/bookings/export.csv`,{headers:{Authorization:`Bearer ${token}`}}).then(r=>r.blob()).then(b=>{const u=URL.createObjectURL(b);const a=document.createElement("a");a.href=u;a.download="bookings.csv";a.click();URL.revokeObjectURL(u)})};
  return <div className="panel">
    <div className="statgrid">
      <Stat label="Today" value={todayCount} testid="stat-today"/>
      <Stat label="Upcoming" value={upcoming} testid="stat-upcoming"/>
      <Stat label="Pending" value={pending} testid="stat-pending"/>
      <Stat label="Total" value={items.length} testid="stat-total"/>
    </div>
    <div className="panel-toolbar">
      <div className="filters">
        <select value={filter.status} onChange={e=>setFilter({...filter,status:e.target.value})} data-testid="bookings-status-filter">
          <option value="">All statuses</option>
          {["pending","confirmed","completed","cancelled","no_show"].map(x=><option key={x} value={x}>{x}</option>)}
        </select>
        <input type="date" value={filter.from} onChange={e=>setFilter({...filter,from:e.target.value})} data-testid="bookings-from-filter"/>
        <input type="date" value={filter.to} onChange={e=>setFilter({...filter,to:e.target.value})} data-testid="bookings-to-filter"/>
      </div>
      <div className="toolbar-actions">
        <button className="button ghost-dark" onClick={exportCsv} data-testid="bookings-export"><Download size={15}/> Export CSV</button>
        <button className="button gold" onClick={()=>setShowAdd(true)} data-testid="bookings-add"><Plus size={15}/> Add walk-in</button>
      </div>
    </div>
    {items.length?<div className="bookings-table" data-testid="bookings-table">
      <div className="row head"><span>Client</span><span>When</span><span>Service</span><span>Status</span><span>Actions</span></div>
      {items.map(b=><BookingRow key={b.id||`${b.phone}-${b.date}-${b.timeSlot}`} b={b} onStatus={setStatus} onEdit={()=>setShowEdit(b)} onDelete={()=>remove(b.id)}/>)}
    </div>:<Empty>No bookings match these filters. New requests will show here.</Empty>}
    {showAdd&&<WalkInModal token={token} onClose={()=>setShowAdd(false)} onSaved={()=>{setShowAdd(false);load()}}/>}
    {showEdit&&<EditBookingModal token={token} booking={showEdit} onClose={()=>setShowEdit(null)} onSaved={()=>{setShowEdit(null);load()}}/>}
  </div>;
}

function BookingRow({b,onStatus,onEdit,onDelete}){
  const wa=`https://wa.me/91${b.phone}?text=${encodeURIComponent(`Hi ${b.name}, this is Smit Studio confirming your appointment on ${b.date} at ${b.timeSlot} for ${b.serviceSummary}. See you soon!`)}`;
  return <div className="row" data-testid={`booking-row-${b.id}`}>
    <span><b>{b.name}</b><small>{b.phone}</small></span>
    <span>{b.date}<small>{b.timeSlot}</small></span>
    <span>{b.serviceSummary}{b.notes&&<small>{b.notes}</small>}</span>
    <select value={b.status} onChange={e=>onStatus(b.id,e.target.value)} className={`status ${b.status}`} data-testid={`booking-status-${b.id}`}>
      {["pending","confirmed","completed","cancelled","no_show"].map(x=><option key={x} value={x}>{x.replace("_"," ")}</option>)}
    </select>
    <span className="rowactions">
      <a href={wa} target="_blank" rel="noreferrer" title="WhatsApp" data-testid={`booking-wa-${b.id}`}><MessageCircle size={15}/></a>
      <a href={`tel:+91${b.phone}`} title="Call"><Phone size={15}/></a>
      <button onClick={onEdit} title="Edit / reschedule" data-testid={`booking-edit-${b.id}`}><Cog size={15}/></button>
      <button onClick={onDelete} title="Delete" data-testid={`booking-delete-${b.id}`}><Trash2 size={15}/></button>
    </span>
  </div>;
}

function WalkInModal({token,onClose,onSaved}){
  const [f,setF]=useState({name:"",phone:"",serviceSummary:"",date:new Date().toISOString().slice(0,10),timeSlot:"",notes:""});
  const [cats,setCats]=useState([]);const [err,setErr]=useState("");
  useEffect(()=>{fetch(`${API}/services`).then(r=>r.json()).then(d=>setCats(d.map(x=>x.category)))},[]);
  const submit=async e=>{
    e.preventDefault();setErr("");
    const r=await authFetch(token,"/admin/bookings",{method:"POST",body:JSON.stringify(f)});
    const d=await r.json(); if(r.ok) onSaved(); else setErr(d.detail||"Could not save.");
  };
  return <Modal onClose={onClose} title="Add walk-in / manual booking">
    <form onSubmit={submit} className="admin-form">
      <label>Name<input required value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="walkin-name"/></label>
      <label>Phone (10 digits)<input required pattern="[6-9][0-9]{9}" value={f.phone} onChange={e=>setF({...f,phone:e.target.value})} data-testid="walkin-phone"/></label>
      <label>Service<select required value={f.serviceSummary} onChange={e=>setF({...f,serviceSummary:e.target.value})} data-testid="walkin-service"><option value="">Choose</option>{cats.map(c=><option key={c}>{c}</option>)}</select></label>
      <div className="formrow">
        <label>Date<input type="date" required value={f.date} onChange={e=>setF({...f,date:e.target.value})} data-testid="walkin-date"/></label>
        <label>Time slot<input required placeholder="e.g. 4:30 PM" value={f.timeSlot} onChange={e=>setF({...f,timeSlot:e.target.value})} data-testid="walkin-time"/></label>
      </div>
      <label>Notes<textarea value={f.notes} onChange={e=>setF({...f,notes:e.target.value})} data-testid="walkin-notes"/></label>
      {err&&<p className="admin-error">{err}</p>}
      <button className="button gold" data-testid="walkin-save">Save booking</button>
    </form>
  </Modal>;
}

function EditBookingModal({token,booking,onClose,onSaved}){
  const [f,setF]=useState({date:booking.date,timeSlot:booking.timeSlot,status:booking.status,notes:booking.notes||"",serviceSummary:booking.serviceSummary});
  const [err,setErr]=useState("");
  const submit=async e=>{
    e.preventDefault();setErr("");
    const r=await authFetch(token,`/admin/bookings/${booking.id}`,{method:"PATCH",body:JSON.stringify(f)});
    if(r.ok) onSaved(); else{const d=await r.json();setErr(d.detail||"Could not save.")}
  };
  return <Modal onClose={onClose} title={`Edit booking — ${booking.name}`}>
    <form onSubmit={submit} className="admin-form">
      <div className="formrow">
        <label>Date<input type="date" value={f.date} onChange={e=>setF({...f,date:e.target.value})} data-testid="edit-date"/></label>
        <label>Time<input value={f.timeSlot} onChange={e=>setF({...f,timeSlot:e.target.value})} data-testid="edit-time"/></label>
      </div>
      <label>Service<input value={f.serviceSummary} onChange={e=>setF({...f,serviceSummary:e.target.value})} data-testid="edit-service"/></label>
      <label>Status<select value={f.status} onChange={e=>setF({...f,status:e.target.value})} data-testid="edit-status">{["pending","confirmed","completed","cancelled","no_show"].map(x=><option key={x} value={x}>{x.replace("_"," ")}</option>)}</select></label>
      <label>Notes<textarea value={f.notes} onChange={e=>setF({...f,notes:e.target.value})} data-testid="edit-notes"/></label>
      {err&&<p className="admin-error">{err}</p>}
      <button className="button gold" data-testid="edit-save">Save changes</button>
    </form>
  </Modal>;
}

// ---------- Menu Manager ----------
function MenuManager({token}){
  const [items,setItems]=useState([]);
  const [showAdd,setShowAdd]=useState(false);
  const [editing,setEditing]=useState(null);
  const load=useCallback(async()=>{const r=await authFetch(token,"/admin/services");setItems(await r.json())},[token]);
  useEffect(()=>{load()},[load]);
  const byCat=items.reduce((a,x)=>{(a[x.category]=a[x.category]||[]).push(x);return a},{});
  const remove=async id=>{if(!confirm("Delete this service?"))return;await authFetch(token,`/admin/services/${id}`,{method:"DELETE"});load()};
  const update=async(id,changes)=>{await authFetch(token,`/admin/services/${id}`,{method:"PATCH",body:JSON.stringify(changes)});load()};
  return <div className="panel">
    <div className="panel-toolbar">
      <p className="panel-hint">Update service names, prices and visibility. Changes reflect on the live site immediately. Set price to blank + visibility "hide" to show "On request".</p>
      <button className="button gold" onClick={()=>setShowAdd(true)} data-testid="menu-add"><Plus size={15}/> Add service</button>
    </div>
    {Object.entries(byCat).map(([cat,list])=><section className="menu-cat" key={cat}>
      <h3>{cat}</h3>
      <div className="menu-table" data-testid={`menu-cat-${cat}`}>
        <div className="row head"><span>Service</span><span>Price ₹</span><span>Visibility</span><span>Duration</span><span>Active</span><span></span></div>
        {list.map(s=><div className="row" key={s.id}>
          <span>{s.name}</span>
          <input type="number" defaultValue={s.price??""} onBlur={e=>update(s.id,{price:e.target.value?Number(e.target.value):null})} data-testid={`menu-price-${s.id}`}/>
          <select value={s.priceVisibility} onChange={e=>update(s.id,{priceVisibility:e.target.value})} data-testid={`menu-vis-${s.id}`}>
            {["inherit","show","hide","from"].map(v=><option key={v} value={v}>{v}</option>)}
          </select>
          <input type="number" defaultValue={s.durationMinutes} onBlur={e=>update(s.id,{durationMinutes:Number(e.target.value)||30})}/>
          <input type="checkbox" checked={!!s.active} onChange={e=>update(s.id,{active:e.target.checked})} data-testid={`menu-active-${s.id}`}/>
          <span className="rowactions">
            <button onClick={()=>setEditing(s)} title="Edit" data-testid={`menu-edit-${s.id}`}><Cog size={14}/></button>
            <button onClick={()=>remove(s.id)} title="Delete" data-testid={`menu-delete-${s.id}`}><Trash2 size={14}/></button>
          </span>
        </div>)}
      </div>
    </section>)}
    {showAdd&&<ServiceModal token={token} onClose={()=>setShowAdd(false)} onSaved={()=>{setShowAdd(false);load()}} categories={Object.keys(byCat)}/>}
    {editing&&<ServiceModal token={token} service={editing} onClose={()=>setEditing(null)} onSaved={()=>{setEditing(null);load()}} categories={Object.keys(byCat)}/>}
  </div>;
}

function ServiceModal({token,service,onClose,onSaved,categories}){
  const [f,setF]=useState(service||{category:categories[0]||"",name:"",price:"",priceVisibility:"inherit",durationMinutes:30,description:"",featured:false,active:true,order:0});
  const submit=async e=>{
    e.preventDefault();
    const body={...f,price:f.price===""||f.price===null?null:Number(f.price),durationMinutes:Number(f.durationMinutes)||30,order:Number(f.order)||0};
    if(service){await authFetch(token,`/admin/services/${service.id}`,{method:"PATCH",body:JSON.stringify(body)})}
    else{await authFetch(token,"/admin/services",{method:"POST",body:JSON.stringify(body)})}
    onSaved();
  };
  return <Modal onClose={onClose} title={service?"Edit service":"Add service"}>
    <form onSubmit={submit} className="admin-form">
      <label>Category<input list="cats" value={f.category} onChange={e=>setF({...f,category:e.target.value})} required data-testid="svc-cat"/><datalist id="cats">{categories.map(c=><option key={c}>{c}</option>)}</datalist></label>
      <label>Service name<input required value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="svc-name"/></label>
      <div className="formrow">
        <label>Price ₹<input type="number" value={f.price??""} onChange={e=>setF({...f,price:e.target.value})} data-testid="svc-price"/></label>
        <label>Visibility<select value={f.priceVisibility} onChange={e=>setF({...f,priceVisibility:e.target.value})} data-testid="svc-vis">{["inherit","show","hide","from"].map(v=><option key={v} value={v}>{v}</option>)}</select></label>
      </div>
      <div className="formrow">
        <label>Duration (min)<input type="number" value={f.durationMinutes} onChange={e=>setF({...f,durationMinutes:e.target.value})} data-testid="svc-dur"/></label>
        <label>Order<input type="number" value={f.order} onChange={e=>setF({...f,order:e.target.value})}/></label>
      </div>
      <label>Description<textarea value={f.description||""} onChange={e=>setF({...f,description:e.target.value})}/></label>
      <div className="formrow">
        <label className="check"><input type="checkbox" checked={!!f.featured} onChange={e=>setF({...f,featured:e.target.checked})}/> Featured</label>
        <label className="check"><input type="checkbox" checked={!!f.active} onChange={e=>setF({...f,active:e.target.checked})}/> Active</label>
      </div>
      <button className="button gold" data-testid="svc-save">{service?"Save changes":"Add service"}</button>
    </form>
  </Modal>;
}

// ---------- Availability ----------
function AvailabilityPanel({token}){
  const [s,setS]=useState(null);
  const [saved,setSaved]=useState(false);
  const load=useCallback(async()=>{const r=await authFetch(token,"/admin/settings");setS(await r.json())},[token]);
  useEffect(()=>{load()},[load]);
  if(!s) return <p>Loading…</p>;
  const save=async(patch)=>{
    const r=await authFetch(token,"/admin/settings",{method:"PATCH",body:JSON.stringify(patch)});
    if(r.ok){const d=await r.json();setS(d.settings);setSaved(true);setTimeout(()=>setSaved(false),1600)}
  };
  const days=[["mon","Monday"],["tue","Tuesday"],["wed","Wednesday"],["thu","Thursday"],["fri","Friday"],["sat","Saturday"],["sun","Sunday"]];
  const wh=s.workingHours||{};
  const setDay=(d,k,v)=>save({workingHours:{...wh,[d]:{...(wh[d]||{}),[k]:v}}});
  return <div className="panel">
    <p className="panel-hint">Working hours drive the client booking calendar. Blackout dates block specific holidays.</p>
    <section className="settings-block">
      <h3>Working hours</h3>
      <div className="settings-grid">
        {days.map(([d,label])=>{const h=wh[d]||{open:"09:00",close:"22:00",closed:false};return <div className="dayrow" key={d}>
          <span>{label}</span>
          <label className="check"><input type="checkbox" checked={!h.closed} onChange={e=>setDay(d,"closed",!e.target.checked)} data-testid={`day-open-${d}`}/> Open</label>
          <input type="time" value={h.open} disabled={h.closed} onChange={e=>setDay(d,"open",e.target.value)} data-testid={`day-from-${d}`}/>
          <input type="time" value={h.close} disabled={h.closed} onChange={e=>setDay(d,"close",e.target.value)} data-testid={`day-to-${d}`}/>
        </div>})}
      </div>
    </section>
    <section className="settings-block">
      <h3>Slot settings</h3>
      <div className="formrow">
        <label>Slot interval (min)<input type="number" defaultValue={s.slotIntervalMinutes} onBlur={e=>save({slotIntervalMinutes:Number(e.target.value)||30})} data-testid="slot-interval"/></label>
        <label>Slot capacity (concurrent chairs)<input type="number" defaultValue={s.slotCapacity} onBlur={e=>save({slotCapacity:Number(e.target.value)||3})} data-testid="slot-capacity"/></label>
        <label>Buffer (min)<input type="number" defaultValue={s.bufferMinutes||0} onBlur={e=>save({bufferMinutes:Number(e.target.value)||0})}/></label>
      </div>
    </section>
    <BlackoutDates dates={s.blackoutDates||[]} save={v=>save({blackoutDates:v})}/>
    {saved&&<p className="saved-hint">Saved.</p>}
  </div>;
}

function BlackoutDates({dates,save}){
  const [d,setD]=useState("");
  return <section className="settings-block">
    <h3>Blackout dates</h3>
    <div className="chiprow" data-testid="blackout-list">{dates.map(x=><span className="chip" key={x}>{x}<button onClick={()=>save(dates.filter(y=>y!==x))} data-testid={`blackout-remove-${x}`}><X size={12}/></button></span>)}</div>
    <div className="addrow"><input type="date" value={d} onChange={e=>setD(e.target.value)} data-testid="blackout-date-input"/><button className="button ghost-dark" onClick={()=>{if(d&&!dates.includes(d)){save([...dates,d].sort());setD("")}}} data-testid="blackout-add">Add blackout</button></div>
  </section>;
}

// ---------- Offers ----------
function OffersManager({token}){
  const [items,setItems]=useState([]);
  const [settings,setSettings]=useState({});
  const [showAdd,setShowAdd]=useState(false);
  const [editing,setEditing]=useState(null);
  const load=useCallback(async()=>{
    const [a,b]=await Promise.all([authFetch(token,"/admin/offers"),authFetch(token,"/admin/settings")]);
    setItems(await a.json()); setSettings(await b.json());
  },[token]);
  useEffect(()=>{load()},[load]);
  const toggleGlobal=async v=>{await authFetch(token,"/admin/settings",{method:"PATCH",body:JSON.stringify({offersEnabled:v})});load()};
  const update=async(id,ch)=>{await authFetch(token,`/admin/offers/${id}`,{method:"PATCH",body:JSON.stringify(ch)});load()};
  const remove=async id=>{if(!confirm("Delete this offer?"))return;await authFetch(token,`/admin/offers/${id}`,{method:"DELETE"});load()};
  return <div className="panel">
    <div className="panel-toolbar">
      <label className="toggle" data-testid="offers-global-toggle-wrap">
        <input type="checkbox" checked={!!settings.offersEnabled} onChange={e=>toggleGlobal(e.target.checked)} data-testid="offers-global-toggle"/>
        <span>Show offers on the live site</span>
      </label>
      <button className="button gold" onClick={()=>setShowAdd(true)} data-testid="offers-add"><Plus size={15}/> Add offer</button>
    </div>
    {items.length?<div className="offers-table" data-testid="offers-table">
      <div className="row head"><span>Title</span><span>Label</span><span>Placement</span><span>Window</span><span>Active</span><span></span></div>
      {items.map(o=><div className="row" key={o.id}>
        <span><b>{o.title}</b>{o.subtitle&&<small>{o.subtitle}</small>}</span>
        <span>{o.discountLabel||"—"}</span>
        <span>{o.placement}</span>
        <span>{o.startDate} → {o.endDate}</span>
        <input type="checkbox" checked={!!o.active} onChange={e=>update(o.id,{active:e.target.checked})} data-testid={`offer-active-${o.id}`}/>
        <span className="rowactions">
          <button onClick={()=>setEditing(o)} data-testid={`offer-edit-${o.id}`}><Cog size={14}/></button>
          <button onClick={()=>remove(o.id)} data-testid={`offer-delete-${o.id}`}><Trash2 size={14}/></button>
        </span>
      </div>)}
    </div>:<Empty>No offers yet. Add one to promote a service — flip the global switch on when you're ready to publish.</Empty>}
    {showAdd&&<OfferModal token={token} onClose={()=>setShowAdd(false)} onSaved={()=>{setShowAdd(false);load()}}/>}
    {editing&&<OfferModal token={token} offer={editing} onClose={()=>setEditing(null)} onSaved={()=>{setEditing(null);load()}}/>}
  </div>;
}

function OfferModal({token,offer,onClose,onSaved}){
  const today=new Date().toISOString().slice(0,10);
  const [f,setF]=useState(offer||{title:"",subtitle:"",discountLabel:"",placement:"offers_section",ctaType:"book",startDate:today,endDate:today,active:true,order:0,image:""});
  const submit=async e=>{
    e.preventDefault();
    const body={...f,order:Number(f.order)||0};
    if(offer){await authFetch(token,`/admin/offers/${offer.id}`,{method:"PATCH",body:JSON.stringify(body)})}
    else{await authFetch(token,"/admin/offers",{method:"POST",body:JSON.stringify(body)})}
    onSaved();
  };
  return <Modal onClose={onClose} title={offer?"Edit offer":"Add offer"}>
    <form onSubmit={submit} className="admin-form">
      <label>Title<input required value={f.title} onChange={e=>setF({...f,title:e.target.value})} data-testid="offer-title"/></label>
      <label>Subtitle<input value={f.subtitle||""} onChange={e=>setF({...f,subtitle:e.target.value})}/></label>
      <label>Label (e.g. "20% off")<input value={f.discountLabel||""} onChange={e=>setF({...f,discountLabel:e.target.value})} data-testid="offer-label"/></label>
      <div className="formrow">
        <label>Placement<select value={f.placement} onChange={e=>setF({...f,placement:e.target.value})} data-testid="offer-placement">{["top_bar","hero_ribbon","offers_section","popup"].map(p=><option key={p} value={p}>{p}</option>)}</select></label>
        <label>CTA<select value={f.ctaType} onChange={e=>setF({...f,ctaType:e.target.value})}>{["book","whatsapp","none"].map(p=><option key={p} value={p}>{p}</option>)}</select></label>
      </div>
      <div className="formrow">
        <label>Start<input type="date" required value={f.startDate} onChange={e=>setF({...f,startDate:e.target.value})} data-testid="offer-start"/></label>
        <label>End<input type="date" required value={f.endDate} onChange={e=>setF({...f,endDate:e.target.value})} data-testid="offer-end"/></label>
      </div>
      <label className="check"><input type="checkbox" checked={!!f.active} onChange={e=>setF({...f,active:e.target.checked})}/> Active</label>
      <button className="button gold" data-testid="offer-save">{offer?"Save":"Add offer"}</button>
    </form>
  </Modal>;
}

// ---------- Settings ----------
function SettingsPanel({token}){
  const [s,setS]=useState(null);
  const [saved,setSaved]=useState(false);
  const load=useCallback(async()=>{const r=await authFetch(token,"/admin/settings");setS(await r.json())},[token]);
  useEffect(()=>{load()},[load]);
  if(!s) return <p>Loading…</p>;
  const save=async patch=>{const r=await authFetch(token,"/admin/settings",{method:"PATCH",body:JSON.stringify(patch)});if(r.ok){const d=await r.json();setS(d.settings);setSaved(true);setTimeout(()=>setSaved(false),1600)}};
  return <div className="panel">
    <section className="settings-block">
      <h3>Announcement bar</h3>
      <label className="toggle"><input type="checkbox" checked={!!s.announcement?.enabled} onChange={e=>save({announcement:{...s.announcement,enabled:e.target.checked}})} data-testid="ann-enabled"/> Show announcement bar</label>
      <label>Message<input defaultValue={s.announcement?.text||""} onBlur={e=>save({announcement:{...s.announcement,text:e.target.value}})} data-testid="ann-text"/></label>
      <div className="formrow">
        <label>Type<select value={s.announcement?.type||"info"} onChange={e=>save({announcement:{...s.announcement,type:e.target.value}})}>{["info","closure"].map(x=><option key={x} value={x}>{x}</option>)}</select></label>
        <label>Link (optional)<input defaultValue={s.announcement?.link||""} onBlur={e=>save({announcement:{...s.announcement,link:e.target.value}})}/></label>
      </div>
    </section>

    <section className="settings-block">
      <h3>Prices</h3>
      <label>Global display mode
        <select value={s.priceDisplayMode||"show"} onChange={e=>save({priceDisplayMode:e.target.value})} data-testid="price-mode">
          <option value="show">Show all prices</option>
          <option value="hide_all">Hide all — show "On request"</option>
        </select>
      </label>
      <label>Price disclaimer<input defaultValue={s.priceDisclaimer||""} onBlur={e=>save({priceDisclaimer:e.target.value})} data-testid="price-disclaimer"/></label>
    </section>

    <section className="settings-block">
      <h3>Contact</h3>
      <label>Phone (display)<input defaultValue={s.contact?.phone||""} onBlur={e=>save({contact:{...s.contact,phone:e.target.value}})}/></label>
      <label>WhatsApp (91xxxxxxxxxx)<input defaultValue={s.contact?.whatsapp||""} onBlur={e=>save({contact:{...s.contact,whatsapp:e.target.value}})}/></label>
      <label>Address<textarea defaultValue={s.contact?.address||""} onBlur={e=>save({contact:{...s.contact,address:e.target.value}})}/></label>
    </section>

    <section className="settings-block">
      <h3>Socials & links</h3>
      <label>Instagram URL<input defaultValue={s.socials?.instagram||""} onBlur={e=>save({socials:{...s.socials,instagram:e.target.value}})}/></label>
    </section>

    <section className="settings-block">
      <h3>Sections</h3>
      <label className="toggle"><input type="checkbox" checked={s.productsWeUseEnabled!==false} onChange={e=>save({productsWeUseEnabled:e.target.checked})} data-testid="products-toggle"/> "Products we use" strip on homepage</label>
      <label className="toggle"><input type="checkbox" checked={!!s.teamEnabled} onChange={e=>save({teamEnabled:e.target.checked})}/> "Our team" section (off until real content added)</label>
    </section>
    {saved&&<p className="saved-hint">Saved.</p>}
  </div>;
}

// ---------- Enquiries ----------
function EnquiriesInbox({token}){
  const [items,setItems]=useState([]);
  const load=useCallback(async()=>{const r=await authFetch(token,"/admin/enquiries");setItems(await r.json())},[token]);
  useEffect(()=>{load()},[load]);
  const set=async(id,status)=>{await authFetch(token,`/admin/enquiries/${id}`,{method:"PATCH",body:JSON.stringify({status})});load()};
  return <div className="panel">
    {items.length?<div className="enq-table" data-testid="enq-table">
      <div className="row head"><span>Client</span><span>Message</span><span>Interest</span><span>Status</span></div>
      {items.map(e=><div className="row" key={e.id}>
        <span><b>{e.name}</b><small>{e.phone}</small></span>
        <span>{e.message||"—"}</span>
        <span>{e.serviceInterest||"—"}</span>
        <select value={e.status} onChange={ev=>set(e.id,ev.target.value)} data-testid={`enq-status-${e.id}`}>{["new","contacted","closed"].map(x=><option key={x} value={x}>{x}</option>)}</select>
      </div>)}
    </div>:<Empty>No enquiries yet. Anything submitted from the contact form or a lost booking attempt will show here.</Empty>}
  </div>;
}

// ---------- Gallery ----------
function GalleryManager({token}){
  const [items,setItems]=useState([]);
  const [uploading,setUploading]=useState(false);
  const fileRef=useRef();
  const load=useCallback(async()=>{const r=await authFetch(token,"/admin/gallery");setItems(await r.json())},[token]);
  useEffect(()=>{load()},[load]);
  const remove=async id=>{if(!confirm("Remove image?"))return;await authFetch(token,`/admin/gallery/${id}`,{method:"DELETE"});load()};
  const upload=async e=>{
    const files=Array.from(e.target.files||[]);if(!files.length)return;setUploading(true);
    for(const file of files){
      const fd=new FormData();fd.append("file",file);
      const r=await fetch(`${API}/admin/gallery/upload`,{method:"POST",headers:{Authorization:`Bearer ${token}`},body:fd});
      if(r.ok){const d=await r.json();
        await authFetch(token,"/admin/gallery",{method:"POST",body:JSON.stringify({imageUrl:d.url,alt:file.name,order:items.length})});
      }else{const d=await r.json();alert(d.detail||"Upload failed")}
    }
    setUploading(false);load();
    if(fileRef.current) fileRef.current.value="";
  };
  const addUrl=async()=>{const u=prompt("Paste an image URL");if(!u)return;await authFetch(token,"/admin/gallery",{method:"POST",body:JSON.stringify({imageUrl:u,alt:"",order:items.length})});load()};
  return <div className="panel">
    <div className="panel-toolbar">
      <p className="panel-hint">Upload real photos of the studio (recommended: bright interior + service-in-progress shots).</p>
      <div className="toolbar-actions">
        <button className="button ghost-dark" onClick={addUrl} data-testid="gallery-addurl">Paste URL</button>
        <label className="button gold" data-testid="gallery-upload-label">
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="gallery-upload-input"/>
          <Upload size={15}/> {uploading?"Uploading…":"Upload photos"}
        </label>
      </div>
    </div>
    {items.length?<div className="gallery-grid" data-testid="gallery-grid">
      {items.map(g=><figure key={g.id}>
        <img src={g.imageUrl.startsWith("/api/")?`${process.env.REACT_APP_BACKEND_URL}${g.imageUrl}`:g.imageUrl} alt={g.alt||""} data-testid={`gallery-item-${g.id}`}/>
        <button onClick={()=>remove(g.id)} data-testid={`gallery-remove-${g.id}`}><Trash2 size={14}/></button>
      </figure>)}
    </div>:<Empty>No images yet — upload the salon's real photos to show on the site.</Empty>}
  </div>;
}

// ---------- Clients CRM ----------
function ClientsCRM({token}){
  const [items,setItems]=useState([]);
  const [q,setQ]=useState("");
  useEffect(()=>{const qs=q?`?q=${encodeURIComponent(q)}`:"";authFetch(token,`/admin/clients${qs}`).then(r=>r.json()).then(setItems)},[token,q]);
  return <div className="panel">
    <div className="panel-toolbar">
      <input placeholder="Search by name or phone" value={q} onChange={e=>setQ(e.target.value)} data-testid="clients-search"/>
    </div>
    {items.length?<div className="clients-table" data-testid="clients-table">
      <div className="row head"><span>Client</span><span>Phone</span><span>Visits</span><span>Last visit</span><span>Services</span><span></span></div>
      {items.map(c=><div className="row" key={c.phone}>
        <span><b>{c.name}</b></span>
        <span>{c.phone}</span>
        <span>{c.visits}</span>
        <span>{c.lastVisit||"—"}</span>
        <span>{[...new Set(c.services)].slice(0,3).join(", ")||"—"}</span>
        <span className="rowactions">
          <a href={`https://wa.me/91${c.phone}`} target="_blank" rel="noreferrer" title="WhatsApp"><MessageCircle size={15}/></a>
          <a href={`tel:+91${c.phone}`}><Phone size={15}/></a>
        </span>
      </div>)}
    </div>:<Empty>No clients yet. Once bookings come in, repeat clients auto-group by phone here.</Empty>}
  </div>;
}

// ---------- Analytics ----------
function Analytics({token}){
  const [data,setData]=useState(null);
  useEffect(()=>{authFetch(token,"/admin/analytics?days=30").then(r=>r.json()).then(setData)},[token]);
  if(!data) return <p>Loading…</p>;
  return <div className="panel">
    <div className="statgrid">
      <Stat label="Bookings · 30d" value={data.total}/>
      <Stat label="New clients" value={data.newClients}/>
      <Stat label="Repeat clients" value={data.repeatClients}/>
      <Stat label="No-show rate" value={`${data.noShowRate||0}%`}/>
    </div>
    <div className="chart-grid">
      <Chart title="Bookings per day" rows={data.byDay.map(x=>({label:x.date.slice(5),value:x.count}))} />
      <Chart title="Top services" rows={data.byService.slice(0,6).map(x=>({label:x.name,value:x.count}))} />
      <Chart title="Top sources" rows={data.bySource.slice(0,6).map(x=>({label:x.name,value:x.count}))} />
      <Chart title="Busiest slots" rows={data.bySlot.slice(0,6).map(x=>({label:x.slot,value:x.count}))} />
    </div>
    <p className="panel-hint">Analytics are computed from real bookings. Numbers grow as your first weeks of appointments come in.</p>
  </div>;
}

function Chart({title,rows}){
  const max=Math.max(1,...rows.map(r=>r.value));
  return <div className="chart"><h4>{title}</h4>{rows.length?<div>{rows.map((r,i)=><div className="bar" key={i}><span>{r.label}</span><span className="track"><span style={{width:`${(r.value/max)*100}%`}}/></span><b>{r.value}</b></div>)}</div>:<Empty small>No data yet.</Empty>}</div>;
}

// ---------- Testimonials ----------
function Testimonials({token}){
  const [items,setItems]=useState([]);
  const [f,setF]=useState({author:"",text:"",sourceUrl:"",active:false,order:0});
  const load=useCallback(async()=>{const r=await authFetch(token,"/admin/testimonials");setItems(await r.json())},[token]);
  useEffect(()=>{load()},[load]);
  const add=async e=>{e.preventDefault();await authFetch(token,"/admin/testimonials",{method:"POST",body:JSON.stringify(f)});setF({author:"",text:"",sourceUrl:"",active:false,order:0});load()};
  const update=async(id,ch)=>{await authFetch(token,`/admin/testimonials/${id}`,{method:"PATCH",body:JSON.stringify(ch)});load()};
  const remove=async id=>{if(!confirm("Delete?"))return;await authFetch(token,`/admin/testimonials/${id}`,{method:"DELETE"});load()};
  return <div className="panel">
    <p className="panel-hint">Paste <b>real</b> reviews only. Nothing goes live until you flip the switch on each entry.</p>
    <form className="admin-form inline" onSubmit={add}>
      <label>Author<input required value={f.author} onChange={e=>setF({...f,author:e.target.value})} data-testid="test-author"/></label>
      <label>Text<textarea required value={f.text} onChange={e=>setF({...f,text:e.target.value})} data-testid="test-text"/></label>
      <label>Source URL (Google, IG comment, etc.)<input value={f.sourceUrl} onChange={e=>setF({...f,sourceUrl:e.target.value})}/></label>
      <button className="button gold" data-testid="test-add">Add testimonial</button>
    </form>
    {items.length?<div className="test-list">
      {items.map(t=><div className="test-card" key={t.id}>
        <p>"{t.text}"</p>
        <div className="test-meta">
          <span>— {t.author}</span>
          <label className="toggle small"><input type="checkbox" checked={!!t.active} onChange={e=>update(t.id,{active:e.target.checked})}/> Live</label>
          <button onClick={()=>remove(t.id)} className="iconbtn"><Trash2 size={14}/></button>
        </div>
      </div>)}
    </div>:<Empty>No testimonials yet. They stay hidden on the site until you add one and switch it live.</Empty>}
  </div>;
}

// ---------- Shared ----------
function Stat({label,value,testid}){return <div className="stat" data-testid={testid||undefined}><b>{value}</b><span>{label}</span></div>}
function Empty({children,small}){return <p className={small?"empty small":"empty"}>{children}</p>}
function Modal({title,onClose,children}){
  useEffect(()=>{const k=e=>{if(e.key==="Escape")onClose()};document.addEventListener("keydown",k);return()=>document.removeEventListener("keydown",k)},[onClose]);
  return <div className="modal-back" onClick={onClose}>
    <div className="modal" onClick={e=>e.stopPropagation()}>
      <header><h3>{title}</h3><button onClick={onClose} aria-label="Close" data-testid="modal-close"><X size={16}/></button></header>
      {children}
    </div>
  </div>;
}
